import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/pro-light-svg-icons/faBolt';
import { TypedMemo } from '../../common/types';
import { CreationPageType } from './inferPageType';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sheet; each part uses its own slice of it */
import styles from './SMLCreationSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** One choice in the override beside the conclusion. */
export interface CreationInferenceOption {
  key: string;
  label: string;
  isOn: boolean;
  /** Left out on the choice already made — there is nothing to select. */
  onSelect?: () => void;
}

interface CreationInferenceBoxProps {
  type: CreationPageType;
  /** One line saying how the sheet reached this. */
  why: string;
  /** Left out where there is nothing to switch to. */
  options?: CreationInferenceOption[];
}

/**
 * The conclusion, not the question.
 *
 * Creation used to open by asking what the artist wanted to make. The release
 * date answers that, so the sheet says what it worked out and puts the other
 * option one tap away for the times it is wrong.
 */
const CreationInferenceBoxComponent: React.FC<CreationInferenceBoxProps> = ({ type, why, options }) => {
  const intl = useIntl();
  const isPresave = type === CreationPageType.presave;

  return (
    <div className={styles.infer}>
      <span className={styles.inferIcon}>
        <FontAwesomeIcon icon={faBolt} />
      </span>
      <span className={styles.inferText}>
        <span className={styles.inferTitle}>
          {isPresave
            ? intl.formatMessage({ id: 'app.sml.create.type.presave', defaultMessage: 'Pre-save page' })
            : intl.formatMessage({ id: 'app.sml.create.type.release', defaultMessage: 'Release page' })}
        </span>
        <span className={styles.inferWhy}>{why}</span>
      </span>
      {options !== undefined && options.length > 0 && (
        <span className={styles.segmented}>
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              className={clsx(styles.segment, option.isOn && styles.segmentOn)}
              onClick={option.onSelect}
            >
              {option.label}
            </button>
          ))}
        </span>
      )}
    </div>
  );
};

export const CreationInferenceBox = TypedMemo(CreationInferenceBoxComponent);
